import React, { useState } from 'react';
import { useField } from 'formik';
import { ChevronsUpDown } from 'lucide-react';
import {
  elementFieldTypeEnums,
  getElement,
  getElementFieldTypeIcon,
} from '@dashboard/services/pageFieldTypes';
import { buttonVariants } from '@dashboard/components/ui/button';
import { cn } from '@dashboard/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@dashboard/components/ui/dropdown-menu';
import { useIsMobile } from '@dashboard/hooks/use-mobile';
import AddPickerDialog from '../AddPicker/AddPickerDialog';
import PickerMenuItems from '../AddPicker/PickerMenuItems';
import { PICKER_MENU_CLASS, pickerSections } from '../AddPicker/pickerItems';
import { SINGLE_MODE } from '../../selectionRules';

const FieldTypeButton = ({ id, name, allowedElements, fields, currentKey }) => {
  const [field, , helpers] = useField(name);
  const [layoutField, , layoutHelpers] = useField('layout');
  const [, , modeHelpers] = useField('selectionMode');
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile();

  // A multi-select is a Dropdown select or a Multiple choice whose Selections allowed is more
  // than one, so the picker shows it as whichever layout it has.
  const isMulti = field.value === elementFieldTypeEnums.MULTI_SELECT;
  const shownValue = isMulti
    ? layoutField.value === 'list'
      ? elementFieldTypeEnums.MULTIPLE_CHOICE
      : elementFieldTypeEnums.SELECT
    : field.value;

  const onPick = value => {
    const isLayoutType =
      value === elementFieldTypeEnums.SELECT || value === elementFieldTypeEnums.MULTIPLE_CHOICE;
    if (isLayoutType) {
      layoutHelpers.setValue(value === elementFieldTypeEnums.MULTIPLE_CHOICE ? 'list' : 'dropdown');
      // Switching between the two layouts keeps a multi-select multi.
      if (isMulti) return;
    }
    // Any other pick is a single element, so Selections allowed must read One again.
    modeHelpers.setValue(SINGLE_MODE);
    helpers.setValue(value);
  };

  const button = (
    <button
      id={id}
      type="button"
      onClick={isMobile ? undefined : () => setOpen(true)}
      className={cn(
        buttonVariants({ variant: 'outline' }),
        'w-full justify-start gap-2 px-2.5 font-normal',
      )}
    >
      <span className="flex size-4 shrink-0 items-center">
        {getElementFieldTypeIcon(shownValue)}
      </span>
      <span className="flex-1 truncate text-left">
        {getElement(shownValue)?.label ?? 'Select a field type'}
      </span>
      <ChevronsUpDown className="size-4 shrink-0 opacity-50" />
    </button>
  );

  if (isMobile) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>{button}</DropdownMenuTrigger>
        <DropdownMenuContent align="start" className={PICKER_MENU_CLASS}>
          <PickerMenuItems
            sections={pickerSections({ mode: 'change', allowedElements, fields, currentKey })}
            selectedId={shownValue}
            onPick={item => item.element !== shownValue && onPick(item.element)}
          />
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <>
      {button}
      {open && (
        <AddPickerDialog
          open
          mode="change"
          onOpenChange={setOpen}
          allowedElements={allowedElements}
          fields={fields}
          currentKey={currentKey}
          initialId={shownValue}
          onConfirm={item => {
            setOpen(false);
            if (item.element !== shownValue) onPick(item.element);
          }}
        />
      )}
    </>
  );
};

export default FieldTypeButton;
